import React, { useRef, useState, useEffect } from 'react';
import IndustryCard from './IndustryCard';
import '../styles/IndustriesSection.css';

const IndustriesSection: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const industries = [
    {
      id: 'healthcare',
      number: '01',
      title: 'Healthcare',
      description: 'Digital health solutions, medical data management, and healthcare infrastructure modernization',
      link: '/industries/healthcare-life-sciences',
      icon: "/assets/healthcare.svg"
    },
    {
      id: 'fintech',
      number: '02',
      title: 'Fintech',
      description: 'Payment processing, digital banking, and financial technology infrastructure',
      link: '/industries/finance-banking',
      icon: "/assets/fintech.svg"
    },
    {
      id: 'ai-ml',
      number: '03',
      title: 'AI/ML & Data Science',
      description: 'Machine learning infrastructure, data analytics, and AI-powered solutions',
      link: '/industries/machine-learning-ai',
      icon: "/assets/ml.svg"
    },
    {
      id: 'government',
      number: '04',
      title: 'Government',
      description: 'Digital transformation, public sector modernization, and citizen service platforms',
      link: '/industries/government-public-sector',
      icon: "/assets/government.svg"
    },
    {
      id: 'saas',
      number: '05',
      title: 'SaaS & Digital Platforms',
      description: 'Cloud-based software solutions and scalable digital platforms',
      link: '/industries/saas',
      icon: "/assets/saas.svg"
    },
    {
      id: 'retail',
      number: '06',
      title: 'Retail & E-commerce',
      description: 'E-commerce solutions, inventory management, and retail technology infrastructure',
      link: '/industries/retail-ecommerce',
      icon: "/assets/retail.svg"
    },
    {
      id: 'manufacturing',
      number: '07',
      title: 'Manufacturing & IoT',
      description: 'Industrial automation, IoT solutions, and smart manufacturing systems',
      link: '/industries/manufacturing-industry-4',
      icon: "/assets/manufacturing.svg"
    },
    {
      id: 'energy-utilities',
      number: '08',
      title: 'Energy & Utilities',
      description: 'Smart grid solutions, energy management systems, and utility infrastructure modernization',
      link: '/industries/energy-utilities',
      icon: "/assets/energy.svg"
    }
  ];

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 300; // Width of card
      const currentScrollLeft = scrollRef.current.scrollLeft;
      const newScrollLeft = direction === 'left' 
        ? currentScrollLeft - scrollAmount 
        : currentScrollLeft + scrollAmount;
      
      scrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const checkScrollPosition = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollPosition();
  }, []);

  return (
    <section className="industries-section-is">
      <div className="industries-header-is">
        <div className="industries-title-wrapper-is">
          <h2 className="industries-title-is">Industries We Serve</h2>
          <p className="industries-subtitle-is">Tailored solutions for diverse sectors</p>
        </div>
        <div className="industries-navigation-is">
          <button 
            className={`nav-button-is nav-button-left-is ${!canScrollLeft ? 'disabled' : ''}`}
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
          >
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button 
            className={`nav-button-is nav-button-right-is ${!canScrollRight ? 'disabled' : ''}`}
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
          >
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="industries-background-pattern-is"></div>

      <div 
        className="industries-scroll-container-is"
        ref={scrollRef}
        onScroll={checkScrollPosition}
      >
        <div className="industries-grid-is">
          {industries.map((industry) => (
            <IndustryCard
              key={industry.id}
              id={industry.id}
              number={industry.number}
              icon={industry.icon}
              title={industry.title}
              description={industry.description}
              link={industry.link}
              isClickable={!!industry.link}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection; 
